import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { action, makeObservable } from 'mobx';

import { ActivityData, TrackUserActivity, TrackUserActivityPayload } from '../models';
import { getLaunchTraceId } from '../services/launch-telemetry';
import { soundService } from '../services/soundService';
import { FrontErrorCodes, GameErrorCodes } from '../enums';
import { getDeviceType, uuid } from '../helpers';
import { CCEuro24GameId } from '../constants';
import { spaVersion } from '../environment';
import { launcherSocket } from '../api';
import type { RootStore } from '.';

const DEFAULT_IDLE_TIMEOUT_MS = 60_000 * 10; // 10 minutes

// Events kept while the launcher hub is not connected; when full, the oldest is dropped.
const MAX_PENDING_ACTIVITY = 200;

type LoadMark = 'launchInfoStart' | 'launchInfoEnd' | 'iframeSet' | 'iframeLoaded';

// A single value keeps the historical { value } wrapper; a flat object goes as it is.
const toActivityData = (data?: ActivityData | null): TrackUserActivity['data'] => {
  if (data === undefined || data === null) return null;
  return typeof data === 'object' ? data : { value: data };
};

export class UserActivityStore {

  private rootStore: RootStore;

  public gameInitTime: number = Date.now();

  private idleTimer: number | null = null;

  // Events not yet accepted by the hub, oldest first. Lives only in memory: a closed tab loses it.
  private pendingActivity: Array<TrackUserActivity> = [];

  // Becomes false on the first game switch: Game Ready then reports a switch, not the page's first game.
  private isFirstLoad = true;

  // performance.now() at each load phase of the current game, for the Game Ready phase keys.
  private loadMarks: Partial<Record<LoadMark, number>> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      resetIdleTimer: action.bound
    });

    this.attachEventListeners(window);
    this.resetIdleTimer();
  }

  public resetIdleTimer(): void {
    soundService.resumeAudioContext();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    this.idleTimer = window.setTimeout(() => {
      this.handleIdleTimeout();
    }, DEFAULT_IDLE_TIMEOUT_MS);
  }

  public attachEventListeners(target: Window | null): void {
    target?.addEventListener('click', this.resetIdleTimer, true);
  }

  private handleIdleTimeout(): void {
    if (this.rootStore.gameStore.gameId === CCEuro24GameId) {
      // For CCEuro24, we want to keep the session alive even if the user is idle, as the game itself has an idle timer and will disconnect the user after a certain period of inactivity.
      // This way, we can avoid unnecessary session terminations and provide a better user experience.
      return;
    }
    this.rootStore.modalStore.openSessionTerminatedModal({
      errorMessage: '',
      errorCode: GameErrorCodes.SessionExpired,
      launcherFrontErrorCode: FrontErrorCodes.IdleTimeout
    });
  }

  public markLoad(mark: LoadMark): void {
    this.loadMarks[mark] = performance.now();
  }

  // A game switch is a load of its own: the next Game Ready measures from its launch-info request.
  public beginGameSwitch(): void {
    this.isFirstLoad = false;
    this.loadMarks = {};
    this.markLoad('launchInfoStart');
  }

  public trackGameReady(): void {
    const readyAt = performance.now();
    const { launchInfoStart, launchInfoEnd, iframeSet, iframeLoaded } = this.loadMarks;
    const data: Record<string, string | number> = {
      value: Date.now() - this.gameInitTime,
      firstLoad: String(this.isFirstLoad),
      // from page start, so it includes the HTML and bundle download
      pageMs: Math.round(readyAt)
    };
    // A phase that did not happen (e.g. gameReady before the iframe load event) is left out, not guessed.
    const addPhase = (key: string, from?: number, to?: number): void => {
      if (from === undefined || to === undefined || to < from) return;
      data[key] = Math.round(to - from);
    };
    addPhase('launchInfoMs', launchInfoStart, launchInfoEnd);
    addPhase('iframeMs', iframeSet, iframeLoaded);
    addPhase('gameMs', iframeLoaded, readyAt);

    this.sendTrackUserActivity({
      category: UserActivityCategory.LoadTime,
      type: UserActivityTypes.GameReady,
      data
    });
  }

  public sendTrackUserActivity(payload: TrackUserActivityPayload): void {
    const { gameId, partnerId } = this.rootStore.gameStore;
    // Built when it happens, so `date` is the event's time even when the hub only takes it later.
    this.pendingActivity.push({
      ...payload,
      eventId: uuid(),
      launchId: getLaunchTraceId(),
      spaVersion,
      device: getDeviceType(),
      gameId,
      partnerId,
      date: new Date().toISOString(),
      data: toActivityData(payload.data),
    });
    if (this.pendingActivity.length > MAX_PENDING_ACTIVITY) this.pendingActivity.shift();
    this.flushPendingActivity();
  }

  /**
   * Sends queued events oldest first and stops at the first one the hub does not take (not connected).
   * Runs on every event and, from the launcher socket, on every connect and reconnect. No other retries.
   */
  public flushPendingActivity(): void {
    while (this.pendingActivity.length && launcherSocket.trackUserActivity(this.pendingActivity[0])) {
      this.pendingActivity.shift();
    }
  }

}
